import React from 'react';
import { motion } from 'motion/react';
import { useAdaptiveAnimation } from '../../../hooks/useAdaptiveAnimation';
import { cn } from '../../../lib/utils';

/**
 * PremiumLoader component props interface
 * Defines the structure for premium loading indicators
 */
export interface PremiumLoaderProps {
  /** Size variant for the loader */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Custom CSS classes for additional styling */
  className?: string;
  /** Loading message to display */
  message?: string;
  /** Whether to show the loading message */
  showMessage?: boolean;
  /** Whether to show as overlay/fullscreen */
  overlay?: boolean;
  /** Custom overlay background color */
  overlayColor?: string;
  /** Animation variant */
  variant?: 'spinner' | 'pulse' | 'dots' | 'ring-3';
}

/**
 * PremiumLoader Component - Brand-Aligned Loading Indicator
 *
 * Creates premium loading indicators that match the AmalQ brand aesthetic
 * using emerald/teal gradients, glass-morphism effects, and smooth animations.
 *
 * Features:
 * - Brand-aligned color scheme with emerald and teal gradients
 * - Multiple animation variants (spinner, pulse, dots, ring)
 * - Glass-morphism overlay option
 * - Adaptive animation system integration
 * - Accessibility compliant with ARIA labels
 * - Hardware-accelerated animations
 * - Responsive sizing system
 *
 * @param props - PremiumLoader component properties
 * @returns React.JSX.Element - Premium loading indicator component
 */
export const PremiumLoader: React.FC<PremiumLoaderProps> = ({
  size = 'md',
  className,
  message = 'Loading...',
  showMessage = false,
  overlay = false,
  overlayColor = 'bg-black/20 backdrop-blur-sm',
  variant = 'spinner',
}) => {
  const { getAnimationProps, shouldAnimate, reducedMotion } = useAdaptiveAnimation();

  // Size configurations
  const sizeConfig = {
    sm: { container: 'w-6 h-6', ring: 'w-5 h-5', dots: 'w-1 h-1', text: 'text-sm' },
    md: { container: 'w-10 h-10', ring: 'w-8 h-8', dots: 'w-1.5 h-1.5', text: 'text-base' },
    lg: { container: 'w-14 h-14', ring: 'w-12 h-12', dots: 'w-2 h-2', text: 'text-lg' },
    xl: { container: 'w-20 h-20', ring: 'w-16 h-16', dots: 'w-3 h-3', text: 'text-xl' },
  };

  const config = sizeConfig[size];

  // Fade-in animation for the entire loader
  const fadeProps = getAnimationProps('fadeIn', 0.3, 0);

  /**
   * Renders spinner variant with rotating ring
   */
  const renderSpinner = (): React.JSX.Element => (
    <div className={cn('relative', config.container)}>
      {/* Outer Ring */}
      <motion.div
        className={cn(
          'absolute inset-0 rounded-full',
          'border-2 border-emerald-200/30 dark:border-emerald-800/30'
        )}
        animate={reducedMotion ? {} : { rotate: 360 }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'linear',
        }}
      />

      {/* Inner Ring with Gradient */}
      <motion.div
        className={cn(
          'absolute inset-1 rounded-full',
          'border-2 border-transparent',
          'bg-linear-to-r from-emerald-500 via-teal-500 to-emerald-500',
          'bg-clip-border'
        )}
        style={{
          background: 'conic-gradient(from 0deg, #059669, #0d9488, #059669)',
          borderRadius: '50%',
          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          maskComposite: 'exclude',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
        }}
        animate={reducedMotion ? {} : { rotate: -360 }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'linear',
        }}
      />

      {/* Center Dot */}
      <div className={cn(
        'absolute inset-1/2 -translate-x-1/2 -translate-y-1/2',
        'w-1 h-1 rounded-full',
        'bg-linear-to-r from-emerald-500 to-teal-500'
      )} />
    </div>
  );

  /**
   * Renders pulse variant with scaling animation
   */
  const renderPulse = (): React.JSX.Element => (
    <motion.div
      className={cn(
        'relative rounded-full',
        config.container,
        'bg-linear-to-r from-emerald-500 via-teal-500 to-emerald-500',
        'shadow-lg shadow-emerald-500/25'
      )}
      animate={reducedMotion ? {} : {
        scale: [1, 1.2, 1],
        opacity: [0.7, 1, 0.7],
      }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
    >
      {/* Inner glow effect */}
      <div className={cn(
        'absolute inset-1 rounded-full',
        'bg-linear-to-r from-emerald-400 to-teal-400',
        'blur-xs opacity-75'
      )} />
    </motion.div>
  );

  /**
   * Renders dots variant with staggered animation
   */
  const renderDots = (): React.JSX.Element => (
    <div className={cn('flex items-center space-x-1', config.container)}>
      {Array.from({ length: 3 }).map((_, index) => (
        <motion.div
          key={index}
          className={cn(
            'rounded-full',
            config.dots,
            'bg-linear-to-r from-emerald-500 to-teal-500'
          )}
          animate={reducedMotion ? {} : {
            scale: [1, 1.5, 1],
            opacity: [0.4, 1, 0.4],
          }}
          transition={{
            duration: 1.2,
            repeat: Infinity,
            ease: [0.25, 0.46, 0.45, 0.94],
            delay: index * 0.2,
          }}
        />
      ))}
    </div>
  );

  /**
   * Renders ring variant with rotating segments
   */
  const renderRing = (): React.JSX.Element => (
    <div className={cn('relative', config.container)}>
      {/* Background Ring */}
      <div className={cn(
        'absolute inset-0 rounded-full',
        'border-2 border-emerald-200/20 dark:border-emerald-800/20'
      )} />

      {/* Animated Segments */}
      {Array.from({ length: 8 }).map((_, index) => (
        <motion.div
          key={index}
          className={cn(
            'absolute rounded-full',
            'bg-linear-to-r from-emerald-500 to-teal-500'
          )}
          style={{
            width: size === 'sm' ? '2px' : size === 'md' ? '3px' : size === 'lg' ? '4px' : '6px',
            height: size === 'sm' ? '8px' : size === 'md' ? '12px' : size === 'lg' ? '16px' : '20px',
            top: '2px',
            left: '50%',
            transformOrigin: '50% 50%',
            transform: `translateX(-50%) rotate(${index * 45}deg)`,
          }}
          animate={reducedMotion ? {} : {
            opacity: [0.3, 1, 0.3],
            scale: [0.8, 1.2, 0.8],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: index * 0.1,
          }}
        />
      ))}
    </div>
  );

  /**
   * Renders the appropriate loader variant
   */
  const renderLoader = (): React.JSX.Element => {
    switch (variant) {
      case 'spinner':
        return renderSpinner();
      case 'pulse':
        return renderPulse();
      case 'dots':
        return renderDots();
      case 'ring-3':
        return renderRing();
      default:
        return renderSpinner();
    }
  };

  // Loader content
  const loaderContent = (
    <motion.div
      className={cn(
        'flex flex-col items-center justify-center space-y-4',
        className
      )}
      {...(shouldAnimate ? fadeProps : {})}
      role="status"
      aria-label="Loading content"
      aria-live="polite"
    >
      {/* Loader Animation */}
      {renderLoader()}

      {/* Loading Message */}
      {showMessage && (
        <motion.p
          className={cn(
            'font-jakarta font-medium',
            'text-gray-600 dark:text-gray-300',
            config.text
          )}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.3 }}
        >
          {message}
        </motion.p>
      )}
    </motion.div>
  );

  // Return overlay version if requested
  if (overlay) {
    return (
      <motion.div
        className={cn(
          'fixed inset-0 z-50 flex items-center justify-center',
          overlayColor
        )}
        {...(shouldAnimate ? fadeProps : {})}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {loaderContent}
      </motion.div>
    );
  }

  return loaderContent;
};

export default PremiumLoader;
